/**
=========================================================
* Soft UI Dashboard React - v3.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/


import { THEME_COLORS } from "constants";

function navbar(theme, ownerState) {
  const { palette, boxShadows, functions, transitions, breakpoints, borders } = theme;
  const { transparentNavbar, absolute, light } = ownerState;

  const { dark, white, text, transparent } = palette;
  const { navbarBoxShadow } = boxShadows;
  const { rgba, pxToRem } = functions;
  const { borderRadius } = borders;

  return {
    boxShadow: transparentNavbar || absolute ? "none" : navbarBoxShadow,
    backdropFilter: transparentNavbar || absolute ? "none" : `saturate(200%) blur(${pxToRem(30)})`,
    backgroundColor:
      transparentNavbar || absolute ? `${transparent.main} !important` : rgba(white.main, 0.8),

    color: () => {
      let color;

      if (light) {
        color = white.main;
      } else if (transparentNavbar) {
        color = text.main;
      } else {
        color = dark.main;
      }

      return color;
    },
    top: absolute ? 0 : pxToRem(12),
    minHeight: pxToRem(75),
    display: "grid",
    alignItems: "center",
    borderRadius: borderRadius.xl,
    paddingTop: pxToRem(8),
    paddingBottom: pxToRem(8),
    paddingRight: absolute ? pxToRem(8) : 0,
    paddingLeft: absolute ? pxToRem(16) : 0,

    "& > *": {
      transition: transitions.create("all", {
        easing: transitions.easing.easeInOut,
        duration: transitions.duration.standard,
      }),
    },

    "& .MuiToolbar-root": {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",

      [breakpoints.up("sm")]: {
        minHeight: "auto",
        padding: `${pxToRem(4)} ${pxToRem(16)}`,
      },
    },
  };
}

const navbarContainer = ({ breakpoints }) => ({
  flexDirection: "column",
  alignItems: "flex-start",
  justifyContent: "space-between",
  pt: 0.5,
  pb: 0.5,

  [breakpoints.up("md")]: {
    flexDirection: "row",
    alignItems: "center",
    paddingTop: "0",
    paddingBottom: "0",
  },
});

const navbarRow = ({ breakpoints }, { isMini }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",

  [breakpoints.up("md")]: {
    justifyContent: isMini ? "space-between" : "stretch",
    width: isMini ? "100%" : "max-content",
  },

  [breakpoints.up("xl")]: {
    justifyContent: "stretch !important",
    width: "max-content !important",
  },
});

const navbarIconButton = ({ typography: { size }, breakpoints }) => ({
  px: 0.75,

  "& .material-icons, .material-icons-round": {
    fontSize: `${size.md} !important`,
  },

  "& .MuiTypography-root": {
    display: "none",

    [breakpoints.up("sm")]: {
      display: "inline-block",
      lineHeight: 1.2,
      ml: 0.5,
    },
  },
});

const navbarMobileMenu = ({ breakpoints }) => ({
  display: "inline-block",
  lineHeight: 0,

  [breakpoints.up("xl")]: {
    display: "none",
  },
});

const SELECT_WIDTH = 260;

const customSelectStyles = {
  container: (provided) => ({
    ...provided,
    width: SELECT_WIDTH,
    maxWidth: "100%",
  }),
  control: (provided, state) => ({
    ...provided,
    width: "100%",
    minHeight: 40,
    fontSize: 13,
    borderRadius: 10,
    borderColor: state.isFocused ? THEME_COLORS.primary : "#E3E6EA",
    boxShadow: state.isFocused ? `0 0 0 3px ${THEME_COLORS.primary}26` : "none",
    backgroundColor: THEME_COLORS.white,
    cursor: "pointer",
    "&:hover": { borderColor: THEME_COLORS.primary },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: "2px 10px",
  }),
  singleValue: (provided) => ({
    ...provided,
    margin: 0,
    maxWidth: "calc(100% - 4px)",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: "#67748E",
    transition: "transform 0.2s ease",
    transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : "none",
  }),
  menu: (provided) => ({
    ...provided,
    width: SELECT_WIDTH + 40,
    right: 0,
    marginTop: 6,
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #E9ECEF",
    boxShadow: "0 12px 32px rgba(52, 71, 103, 0.16)",
    zIndex: 20,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: 6,
    maxHeight: 320,
  }),
  option: (provided, state) => ({
    ...provided,
    height: "auto",
    minHeight: 44,
    padding: "8px 12px",
    borderRadius: 8,
    cursor: "pointer",
    whiteSpace: "normal",
    wordBreak: "break-word",
    color: state.isSelected ? THEME_COLORS.white : "#344767",
    backgroundColor: state.isSelected
      ? THEME_COLORS.primary
      : state.isFocused
      ? "#F0F9FC"
      : "transparent",
    "&:active": {
      backgroundColor: state.isSelected ? THEME_COLORS.primary : "#E1F4FA",
    },
  }),
};

export {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
  customSelectStyles,
};
